import { Button, Tooltip } from "@material-ui/core";
import { makeStyles, Theme, withStyles } from "@material-ui/core/styles";
import Timeline from "@material-ui/lab/Timeline";
import MuiTimelineConnector from "@material-ui/lab/TimelineConnector";
import MuiTimelineContent from "@material-ui/lab/TimelineContent";
import MuiTimelineDot from "@material-ui/lab/TimelineDot";
import MuiTimelineItem from "@material-ui/lab/TimelineItem";
import TimelineSeparator from "@material-ui/lab/TimelineSeparator";
import { RouteComponentProps } from "@reach/router";
import clsx from "clsx";
import { COLORS } from "constants/colors";
import useText from "constants/text";
import React from "react";
import styled from "styled-components";
import StatusInfo from "ui/StatusInfo";
import { COURSES_STATUS_COLORS } from "domains/Course/utils";
import { truncateString } from "utils";
import { Action, ActionStatus, ActionType, Course, IActionItem } from "generated/graphql";
import ShowTextOnHover from "./ShowTextOnHover";

const useStyles = makeStyles({
  timeLineStyled: {
    height: "fit-content",
    fontSize: 11,
    position: "absolute",
    marginLeft: 19,
    width: "16rem",
  },
  label1: {
    paddingLeft: 15,
    fontSize: 11,
    color: "#c9c9c9",
    display: "block",
    width: "max-content",
  },
  label2: {
    fontSize: 11,
    color: "#c9c9c9",
    width: "max-content",
    marginLeft: 23,
  },
  status: {
    backgroundColor: "rgba(151, 151, 151, 0.2)",
    padding: "7px 12px",
    borderRadius: 24,
    fontSize: 14,
    fontWeight: 600,
    color: "#979797",
  },
  timeLine: {
    paddingRight: 0,
    marginBottom: 0,
    paddingBottom: 0,
  },
  bottomContainer: {
    float: "right",
    display: "flex",
    alignItems: "center",
  },
  buttonLabel: {
    fontSize: 11,
  },
});
const ButtonStyled = styled(Button)`
  height: 30px;
  width: 102px;
  margin-left: 2px;

  &.pickup.outlined {
    color: #757a93 !important;
    border-color: #757a93 !important;
    margin-right: 16px;
  }

  &.delivery.outlined {
    color: #ebae23;
    border-color: #ebae23;
    margin-right: 16px;
  }
  &.done {
    margin-right: 16px;
    color: #757a93;
    border-color: #757a93;
  }
`;

const TimelineItem = styled(MuiTimelineItem)`
  min-height: 30px;
  &.dot {
    min-height: 20px;
  }
  :before {
    content: none;
  }
`;

const TimelineContent = styled(MuiTimelineContent)`
  font-size: 14px;
  font-weight: 900;
  color: #707070;
  padding: 0 5px;
`;

const TimelineConnector = styled(MuiTimelineConnector)`
  width: 1px;
  &.dot {
    margin-left: 6px;
  }
`;

const TimelineDot = styled(MuiTimelineDot)`
  height: 18px;
  width: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  box-shadow: none;
  margin: 0;
  background-color: transparent;

  &.pickup.outlined {
    color: ${COLORS.primary};
    border-color: ${COLORS.primary};
  }

  &.pickup.contained {
    background-color: ${COLORS.primary};
  }

  &.delivery.outlined {
    color: #ebae23;
    border-color: #ebae23;
  }

  &.delivery.contained {
    background-color: #ebae23;
  }
  &.done {
    color: #c9c9c9;
    border-color: #c9c9c9;
  }

  &.dot {
    height: 5px;
    width: 5px;
    padding: 0;
    margin-left: 7px;
  }
`;

export const CustomTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: "#fff",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 300,
    boxShadow: "1px 1px 1px 1px #ccc",
  },
}))(Tooltip);

const Wrapper = styled.div`
  width: 100%;
`;
interface CourseCardProps extends RouteComponentProps {
  course: Course;
}

export default function CourseCard({ course }: CourseCardProps) {
  const [showActionItems, setShow] = React.useState(true);
  const [items, setItems] = React.useState([]);
  const [actions, setActions] = React.useState<Action[]>([]);
  const { text } = useText();
  const classes = useStyles();

  React.useEffect(() => {
    setShow(true);
    setItems([]);
    const localActions = [...course.actions];
    const actionsSorted = localActions.sort((a, b) => (a.type === ActionType.Pickup ? -1 : 1));
    setActions(actionsSorted);
  }, [course]);

  const shoWParcels = (items: IActionItem[]) => {
    setItems(items || []);
    setShow(!showActionItems);
  };
  const onBack = () => {
    setItems([]);
    setShow(!showActionItems);
  };
  return (
    <>
      {showActionItems ? (
        <Wrapper>
          <Timeline className={classes.timeLine} align="left">
            {actions?.map((action, ind) => (
              <div>
                <div className={classes.bottomContainer}>
                  <StatusInfo
                    background={COURSES_STATUS_COLORS[action?.status].backgroundColor}
                    status={COURSES_STATUS_COLORS[action?.status].text}
                    width="85px"
                    color={COURSES_STATUS_COLORS[action?.status].color}
                  />
                </div>

                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      className={clsx(action.type === ActionType.Delivery ? "contained" : "outlined", {
                        delivery: true,
                      })}
                    >
                      {action.type === ActionType.Pickup ? "P" : "D"}
                    </TimelineDot>

                    {ind !== actions.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent
                    style={{ color: action.status === ActionStatus.Done ? "#c9c9c9" : "" }}
                    className={classes.timeLineStyled}
                  >
                    <ShowTextOnHover title={action.address.address}>
                      <span>{truncateString(action.address.address, 20)}</span>
                    </ShowTextOnHover>
                  </TimelineContent>
                </TimelineItem>

                {ind !== actions.length - 1 ? (
                  <TimelineItem className="dot">
                    <TimelineSeparator>
                      <TimelineDot
                        className={clsx("dot", "outlined", {
                          delivery: true,
                        })}
                      ></TimelineDot>

                      <TimelineConnector className="dot"></TimelineConnector>
                    </TimelineSeparator>
                    <TimelineContent></TimelineContent>
                  </TimelineItem>
                ) : (
                  ""
                )}
              </div>
            ))}
          </Timeline>
        </Wrapper>
      ) : (
        <div></div>
      )}
    </>
  );
}
